/**
 * Pilotage de la sélection de palier.
 *
 * Les paliers n'existent pas dans les données produit : ce sont les attributs
 * data-* des tuiles (blocks/_pack-tier.liquid) qui portent tout — quantité,
 * montants, mode de choix du coloris, contenu imposé, visuel.
 *
 * ⚠️ Le montant facturé est celui de la variante du produit de pack. Voir
 * l'en-tête de blocks/product-pack-tiers.liquid pour la correspondance.
 */

const TIER_INPUT = 'input[name="pack_tier"]';

/** @returns {HTMLInputElement | null} */
function selectedTier() {
  return document.querySelector(`${TIER_INPUT}:checked`);
}

/**
 * Le mode est posé sur <html> plutôt que sur un conteneur : le sélecteur de
 * coloris à masquer appartient au picker natif, qui n'est pas un descendant de
 * nos blocs. C'est le seul ancêtre commun garanti.
 * @param {HTMLInputElement | null} tier
 */
function applyMode(tier) {
  document.documentElement.dataset.packMode = tier?.dataset.selectionMode || 'choice';
}

/**
 * « Noir:2, Blanc:2, Beige:1 » → « 2X Noir - 2X Blanc - 1X Beige »
 *
 * La quantité est toujours écrite, même à 1 : c'est ce qui rend les paliers
 * comparables d'un coup d'œil, et ce qui lève l'ambiguïté sur un pack de 5 où
 * deux coloris sont doublés et le troisième non.
 * @param {string} raw
 */
function formatContents(raw) {
  return (raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [name, count] = part.split(':').map((piece) => piece.trim());
      const quantity = parseInt(count ?? '1', 10);
      const n = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      return `${n}X ${name}`;
    })
    .join(' - ');
}

/**
 * Contenu désigné par une vignette pour le palier retenu. Une vignette de
 * coloris vaut ce coloris autant de fois que le palier compte de pièces ; une
 * vignette à contenu fixe vaut son contenu. Même règle que dans pack-cart.js.
 * @param {HTMLInputElement} duo
 * @param {HTMLInputElement | null} tier
 */
function contentsFor(duo, tier) {
  const colour = duo.dataset.colour || '';
  if (colour) return `${colour}:${parseInt(tier?.dataset.quantity || '1', 10) || 1}`;
  return duo.dataset.contents || '';
}

/**
 * « 1:29,90 | 3:24,90 » → [{ from: 1, cents: 2990 }, { from: 3, cents: 2490 }]
 * Même écriture que la grille de la composition libre (assets/pack-composer.js).
 * @param {string} raw
 * @returns {{ from: number, cents: number }[]}
 */
function parseGrid(raw) {
  return (raw || '')
    .split('|')
    .map((part) => {
      const [count, price] = part.split(':').map((piece) => piece.trim());
      const from = parseInt(count ?? '', 10);
      const cents = Math.round(parseFloat((price ?? '').replace(/\s/g, '').replace(',', '.')) * 100);
      return { from, cents };
    })
    .filter((step) => Number.isFinite(step.from) && step.from > 0 && Number.isFinite(step.cents) && step.cents > 0)
    .sort((a, b) => a.from - b.from);
}

/**
 * Prix d'une pièce pour ce nombre de pièces : la dernière marche atteinte.
 * @param {{ from: number, cents: number }[]} grid
 * @param {number} count
 */
function pieceCents(grid, count) {
  let cents = grid[0]?.cents ?? 0;
  for (const step of grid) if (count >= step.from) cents = step.cents;
  return cents;
}

/**
 * Remise en pourcentage face aux mêmes pièces au prix d'une seule, arrondie
 * vers le bas : l'annonce ne dit jamais plus que la remise réelle. En entiers,
 * sur les centimes, pour éviter les arrondis flottants (20,07 % → 20).
 * @param {number} fullCents - prix plein
 * @param {number} paidCents - prix payé
 */
function percentOff(fullCents, paidCents) {
  if (!(fullCents > 0) || !(paidCents < fullCents)) return 0;
  return Math.floor(((fullCents - paidCents) * 100) / fullCents);
}

/**
 * Économie face aux mêmes pièces au prix d'une seule.
 * @param {{ from: number, cents: number }[]} grid
 * @param {number} count
 */
function savingCents(grid, count) {
  return (pieceCents(grid, 1) - pieceCents(grid, count)) * count;
}

const money = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: window.Shopify?.currency?.active || 'EUR',
});

/** @param {number} cents */
const formatMoney = (cents) => money.format(cents / 100);

const wholeMoney = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: window.Shopify?.currency?.active || 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** « 5 € » plutôt que « 5,00 € » quand le montant est rond : pour les pastilles.
 * @param {number} cents */
const formatCompactMoney = (cents) => (cents % 100 === 0 ? wholeMoney.format(cents / 100) : formatMoney(cents));

/**
 * Montants à afficher pour le palier coché. Ceux de la tuile, sauf quand les
 * lignes par pièce comptent un autre nombre de pièces — au-delà du plus grand
 * palier : ils sont alors calculés sur la grille du bloc des pièces, avec ses
 * textes.
 * @param {HTMLInputElement} tier
 */
function offerFor(tier) {
  const fromTier = {
    price: tier.dataset.price || '',
    unit: tier.dataset.unit || '',
    note: tier.dataset.note || '',
    compare: tier.dataset.compare || '',
    saving: tier.dataset.saving || '',
    argument: tier.dataset.argument || '',
  };

  const pieces = document.querySelector('pack-pieces');
  if (!(pieces instanceof HTMLElement)) return fromTier;

  const count = parseInt(pieces.dataset.count || '', 10);
  const quantity = parseInt(tier.dataset.quantity || '1', 10) || 1;
  const grid = parseGrid(pieces.dataset.priceGrid || '');
  if (!count || count === quantity || !grid.length) return fromTier;

  const unitCents = pieceCents(grid, count);
  const saving = savingCents(grid, count);
  const shippingFrom = parseInt(pieces.dataset.shippingFrom || '', 10) || 0;

  return {
    price: formatMoney(unitCents * count),
    unit: (pieces.dataset.unitLabel || '').replace('{price}', formatMoney(unitCents)),
    note: shippingFrom && count >= shippingFrom ? pieces.dataset.shippingLabel || '' : '',
    compare: saving > 0 ? formatMoney(pieceCents(grid, 1) * count) : '',
    saving:
      saving > 0
        ? (pieces.dataset.savingLabel || '')
            .replace('{amount}', formatCompactMoney(saving))
            .replace('{percent}', String(percentOff(pieceCents(grid, 1) * count, unitCents * count)))
        : '',
    argument: '',
  };
}

/**
 * Base commune : réagit à la sélection d'un palier, et au nombre de pièces des
 * lignes par pièce (`pack:count`), qui peut dépasser le plus grand palier.
 */
class PackAware extends HTMLElement {
  connectedCallback() {
    this.handleChange = this.handleChange.bind(this);
    this.handleCount = () => this.render(selectedTier());
    document.addEventListener('change', this.handleChange);
    document.addEventListener('pack:count', this.handleCount);
    this.render(selectedTier());
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.handleChange);
    document.removeEventListener('pack:count', this.handleCount);
  }

  /** @param {Event} event */
  handleChange(event) {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'pack_tier') {
      this.render(target);
    }
  }

  /** @param {HTMLInputElement | null} _tier */
  render(_tier) {}
}

/** Montant du palier retenu. */
class PackPrice extends PackAware {
  connectedCallback() {
    this.totalEl = this.querySelector('[data-pack-total]');
    this.unitEl = this.querySelector('[data-pack-unit]');
    this.noteEl = this.querySelector('[data-pack-note]');
    this.compareEl = this.querySelector('[data-pack-compare]');
    this.savingEl = this.querySelector('[data-pack-saving]');
    this.argumentEl = this.querySelector('[data-pack-argument]');
    this.perksEl = this.querySelector('[data-pack-perks]');
    super.connectedCallback();
  }

  /**
   * Écrit la valeur et masque l'élément quand elle est vide : un palier sans
   * économie — l'Unité, le Pack de 2 — ne doit rien afficher plutôt qu'un
   * conteneur vide qui décalerait la ligne.
   * @param {Element | null} el
   * @param {string} value
   */
  static fill(el, value) {
    if (!el) return;
    el.textContent = value;
    if (el instanceof HTMLElement) el.hidden = value === '';
  }

  /**
   * Comme `fill`, le montant en gras : « Vous économisez **5,00 €** ». Le
   * montant est repéré dans le texte, qui vient d'un réglage libre ; sans
   * montant, le texte reste tel quel. Construit en nœuds, jamais en HTML.
   * @param {Element | null} el
   * @param {string} value
   */
  static fillAmount(el, value) {
    if (!el) return;
    // Un montant (« 14 € ») ou, depuis le 2026-10-06, un pourcentage (« 20 % »).
    const match = value.match(/\d[\d\s\u00a0\u202f.,]*[\s\u00a0\u202f]?[€%]/);
    if (!match || match.index === undefined) {
      PackPrice.fill(el, value);
      return;
    }

    const amount = document.createElement('strong');
    amount.textContent = match[0];
    el.replaceChildren(value.slice(0, match.index), amount, value.slice(match.index + match[0].length));
    if (el instanceof HTMLElement) el.hidden = value === '';
  }

  render(tier) {
    if (!tier) return;
    const offer = offerFor(tier);

    if (this.totalEl) this.totalEl.textContent = offer.price;

    PackPrice.fill(this.unitEl, offer.unit);
    PackPrice.fill(this.noteEl, offer.note);
    PackPrice.fill(this.compareEl, offer.compare);
    PackPrice.fillAmount(this.savingEl, offer.saving);
    PackPrice.fill(this.argumentEl, offer.argument);
    // Le bandeau ne porte plus que la livraison : l'économie est passée sur la
    // ligne du montant (2026-10-06).
    if (this.perksEl instanceof HTMLElement) this.perksEl.hidden = !offer.note;
  }
}

/** Contenu imposé du palier retenu : visuel + liste des coloris. */
class PackContents extends PackAware {
  connectedCallback() {
    this.imageEl = this.querySelector('.pack-contents__image');
    this.listEl = this.querySelector('[data-pack-list]');
    super.connectedCallback();
  }

  render(tier) {
    if (!tier) return;

    if (this.imageEl) {
      const src = tier.dataset.image || '';
      this.imageEl.hidden = src === '';
      if (src) {
        this.imageEl.src = src;
        this.imageEl.alt = tier.dataset.imageAlt || '';
      }
    }

    if (this.listEl) this.listEl.textContent = formatContents(tier.dataset.contents);
  }
}

/**
 * Montant porté par le bouton d'ajout au panier.
 *
 * Séparé de `PackPrice`, qui ne regarde que ses propres descendants : le bouton
 * vit dans le bloc d'achat, plusieurs niveaux plus loin dans l'arbre.
 */
class PackCtaPrice extends PackAware {
  connectedCallback() {
    super.connectedCallback();

    // Horizon remplace le contenu du bouton après avoir résolu la variante
    // initiale, puis à chaque changement de taille ou de coloris. Cette mise à
    // jour serveur réinjecte le prix unitaire. Observer le bouton permet de
    // réappliquer le prix du palier retenu une fois le morph terminé.
    this.observer = new MutationObserver(() => this.render(selectedTier()));
    this.observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.observer?.disconnect();
  }

  render(tier) {
    const totalEl = this.querySelector('[data-pack-cta-total]');
    if (!totalEl || !tier) return;
    // Le montant du palier prime, mais on garde celui rendu par le serveur si
    // la tuile n'en porte pas : un bouton sans prix vaut mieux qu'un bouton vide.
    const offer = offerFor(tier);
    const price = offer.price;

    // Prix comparé barré, masqué sur les paliers qui n'en ont pas. Même
    // précaution que pour le montant : ne rien réécrire d'identique.
    const compareEl = this.querySelector('[data-pack-cta-compare]');
    if (compareEl instanceof HTMLElement) {
      if (compareEl.textContent !== offer.compare) compareEl.textContent = offer.compare;
      const hidden = offer.compare === '';
      if (compareEl.hidden !== hidden) compareEl.hidden = hidden;
    }
    // Ne pas réécrire une valeur identique : cela déclencherait inutilement
    // l'observateur et créerait une boucle de mutations.
    if (price && totalEl.textContent !== price) totalEl.textContent = price;
  }
}

if (!customElements.get('pack-price')) customElements.define('pack-price', PackPrice);
if (!customElements.get('pack-cta-price')) customElements.define('pack-cta-price', PackCtaPrice);
if (!customElements.get('pack-contents')) customElements.define('pack-contents', PackContents);

/**
 * Contenu de la vignette retenue, écrit sur la ligne du titre du bloc de
 * vignettes. Les vignettes masquent leur libellé : sans ça, rien ne nomme ce
 * qui est coché. Il dépend aussi du palier — « Noir » vaut 3X Noir sur le Pack
 * de 3 — d'où le rendu à chaque changement de l'un ou de l'autre.
 */
function renderDuoLabel() {
  const target = document.querySelector('[data-pack-duo-label]');
  if (!(target instanceof HTMLElement)) return;

  const duo = document.querySelector('input[name="pack_duo"]:checked');
  const tier = selectedTier();
  // Le contenu formaté plutôt que le libellé libre : les paliers de pack doivent
  // annoncer leur composition dans la même écriture. Sur l'Unité, le coloris
  // seul, comme l'écrivait le picker natif : « 1X Noir » n'y apprend rien.
  //
  // Sur un pack, le contenu des lignes par pièce quand elles sont là : c'est
  // lui qui fait foi, mélange compris (« 2X Noir - 1X Blanc »), et aucune
  // vignette n'est alors cochée.
  const pieces = document.querySelector('pack-pieces');
  let label = '';
  if (tier?.dataset.selectionMode === 'duo' && pieces instanceof HTMLElement && pieces.dataset.contents) {
    label = formatContents(pieces.dataset.contents);
  } else if (duo instanceof HTMLInputElement) {
    label =
      tier?.dataset.selectionMode === 'choice'
        ? duo.dataset.colour || duo.dataset.label || ''
        : formatContents(contentsFor(duo, tier));
  }

  target.textContent = label;
  target.hidden = label === '';
}

/*
 * Coloris partagé entre les vignettes des packs et le sélecteur natif de
 * l'Unité. Décision du principal, 2026-09-11 : chaque palier propose les trois
 * mêmes coloris, et un Beige choisi sur un pack redevenait Noir en repassant à
 * l'Unité.
 *
 * Vignette → natif : on clique le bouton radio natif, masqué sur les packs,
 * pour que ce soit la mécanique du thème qui change la variante, la galerie et
 * l'adresse. Natif → vignette : on coche la vignette sans émettre d'événement,
 * ce qui coupe court à tout aller-retour. Au chargement, c'est le coloris de la
 * fiche qui fait foi : il vient de l'adresse.
 *
 * La correspondance se fait sur la valeur, jamais sur le nom de l'option, qui
 * varie d'un produit à l'autre (« Coloris », « Couleur »). Les valeurs de
 * taille ne peuvent pas se confondre avec un coloris.
 */
const NATIVE_INPUT = '.variant-option input[type="radio"]';

/** @param {string} colour */
function nativeInputFor(colour) {
  for (const input of document.querySelectorAll(NATIVE_INPUT)) {
    if (input instanceof HTMLInputElement && input.value === colour) return input;
  }
  return null;
}

/** @param {string} colour */
function duoInputFor(colour) {
  for (const input of document.querySelectorAll('input[name="pack_duo"]')) {
    if (input instanceof HTMLInputElement && input.dataset.colour === colour) return input;
  }
  return null;
}

/** @param {HTMLInputElement} duo */
function syncNativeFromDuo(duo) {
  const colour = duo.dataset.colour;
  if (!colour) return;

  const input = nativeInputFor(colour);
  if (input && !input.checked) input.click();
}

/** @param {HTMLInputElement} input */
function syncDuoFromNative(input) {
  const duo = duoInputFor(input.value);
  if (!duo || duo.checked) return;

  duo.checked = true;
  renderDuoLabel();
}

function syncDuoFromPage() {
  for (const input of document.querySelectorAll(`${NATIVE_INPUT}:checked`)) {
    if (input instanceof HTMLInputElement) syncDuoFromNative(input);
  }
}

/**
 * Photo des vignettes selon le palier coché : celle de la variante du produit
 * de pack dans le coloris de la vignette, lue dans la carte du palier
 * (blocks/_pack-tier.liquid). Deux débardeurs blancs pour le Pack de 2 Blanc,
 * trois pour le Pack de 3. Décision du principal, 2026-09-11.
 *
 * Sans photo de pack pour ce coloris, ou sur un palier sans produit de pack,
 * la vignette reprend le packshot rendu par le serveur, gardé en `data-*` au
 * premier passage. Un visuel choisi dans l'éditeur n'est jamais remplacé.
 * @param {HTMLInputElement | null} tier
 */
function renderDuoImages(tier) {
  const node = tier ? document.querySelector(`[data-pack-product-map="${CSS.escape(tier.value)}"]`) : null;

  /** @type {{ variants: { options: string[], image: string | null, image_small: string | null }[] } | null} */
  let map = null;
  try {
    map = node?.textContent ? JSON.parse(node.textContent) : null;
  } catch {
    map = null;
  }

  for (const input of document.querySelectorAll('input[name="pack_duo"]')) {
    if (!(input instanceof HTMLInputElement) || input.hasAttribute('data-custom-image')) continue;
    // En pastilles, le packshot est masqué : le changer le téléchargerait pour rien.
    if (input.closest('[data-display="pastilles"]')) continue;

    const image = input.parentElement?.querySelector('.pack-duo-option__image');
    if (!(image instanceof HTMLImageElement)) continue;

    image.dataset.defaultSrc ??= image.getAttribute('src') || '';
    image.dataset.defaultSrcset ??= image.getAttribute('srcset') || '';

    const colour = input.dataset.colour;
    const variant = colour ? map?.variants.find((candidate) => candidate.image && candidate.options.includes(colour)) : null;

    if (variant?.image) {
      image.srcset = variant.image_small ? `${variant.image_small} 200w, ${variant.image} 400w` : '';
      image.src = variant.image;
    } else {
      image.srcset = image.dataset.defaultSrcset;
      image.src = image.dataset.defaultSrc;
    }
  }
}

/**
 * Libellé du palier coché sur la ligne du titre des paliers : « Votre pack :
 * Pack de 3 ». En rangée, les tuiles ne portent que le nombre de pièces.
 * @param {HTMLInputElement | null} tier
 */
function renderTierValue(tier) {
  const target = document.querySelector('[data-pack-tier-value]');
  if (!(target instanceof HTMLElement)) return;

  const label = tier?.dataset.label || '';
  target.textContent = label;
  target.hidden = label === '';
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 */
function createElement(tag, className, text) {
  const element = document.createElement(tag);
  element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

/**
 * Écrit un modèle de réglage dont un repère devient un élément : « {price} la
 * pièce » avec le prix en gras, « Ajouter un {n} débardeur » avec le rang en
 * exposant. Construit en nœuds, jamais en HTML : le texte vient de l'éditeur.
 * @param {HTMLElement} target
 * @param {string} template
 * @param {string} key
 * @param {Node} node
 */
function fillTemplate(target, template, key, node) {
  const [before = '', after = ''] = template.split(key);
  target.append(before, ...(template.includes(key) ? [node] : []), after);
}

/**
 * Valeur cochée dans le picker natif parmi ces valeurs : la taille de la fiche
 * quand on lui passe les tailles, son coloris quand on lui passe les coloris.
 * Correspondance sur la valeur, comme pour les vignettes plus haut.
 * @param {Set<string>} values
 */
function nativeValueIn(values) {
  for (const input of document.querySelectorAll(`${NATIVE_INPUT}:checked`)) {
    if (input instanceof HTMLInputElement && values.has(input.value)) return input.value;
  }
  return '';
}

/**
 * @typedef {{ colour: string, size: string, manual: boolean }} Piece
 * `manual` : taille changée à la main dans la ligne. Sinon, la ligne suit la
 * taille choisie en haut de la fiche.
 */

/**
 * Lignes par pièce du pack (blocks/product-pack-pieces.liquid). Décision du
 * principal, 2026-09-27 : une ligne par débardeur avec sa rangée de pastilles
 * et son menu de taille, « + » pour en ajouter un, « × » pour en retirer un.
 *
 * La taille choisie en haut fait foi : toutes les lignes la suivent, jusqu'à
 * ce que la taille d'une ligne soit changée à la main. Cette ligne garde alors
 * la sienne. Une pièce ajoutée prend la taille du haut.
 *
 * Le nombre de lignes et le palier coché se suivent. Cocher un palier crée ou
 * retire des lignes ; « + » et « × » cochent le palier du nombre de pièces
 * obtenu. Sans palier de ce nombre — au-delà du plus grand —, le plus grand
 * reste coché pour que le mode et le panier gardent un palier de référence,
 * mais `data-pack-beyond` sur <html> le fait paraître décoché, et le montant
 * vient de la grille du bloc (`offerFor`).
 *
 * L'état est publié sur l'élément, pour assets/pack-cart.js qui choisit le
 * produit facturé : `data-count`, `data-contents` (« Noir:2, Blanc:1 »),
 * `data-lines` (pièces regroupées par taille et coloris, en JSON) et
 * `data-mixed` (plus d'un coloris ou plus d'une taille).
 */
class PackPieces extends HTMLElement {
  /** @type {Piece[]} */
  pieces = [];

  /** @type {{ name: string, fill: string, image: string | null, alt: string }[]} */
  options = [];

  /** @type {string[]} Tailles de la fiche, dans l'ordre de l'admin. */
  sizes = [];

  /** Vrai pendant que le composant coche lui-même un palier. */
  syncing = false;

  /** Ligne dont la liste des tailles est ouverte, -1 pour aucune. */
  openSize = -1;

  uid = `pack-pieces-${Math.random().toString(36).slice(2, 8)}`;

  connectedCallback() {
    this.options = this.readJson('[data-pack-colours]');
    this.sizes = this.readJson('[data-pack-sizes]');

    this.list = this.querySelector('[data-pieces-list]');
    this.addButton = this.querySelector('[data-pieces-add]');
    this.max = parseInt(this.dataset.max || '', 10) || 6;
    this.grid = parseGrid(this.dataset.priceGrid || '');
    this.shippingFrom = parseInt(this.dataset.shippingFrom || '', 10) || 0;

    // Le coloris et la taille de départ sont ceux de la fiche.
    const colour = nativeValueIn(new Set(this.options.map((option) => option.name))) || this.options[0]?.name || '';
    const quantity = parseInt(selectedTier()?.dataset.quantity || '1', 10) || 1;
    this.pieces = Array.from({ length: quantity }, () => this.newPiece(colour));

    document.addEventListener('change', this.handleChange);
    document.addEventListener('click', this.handleTierClick);
    document.addEventListener('click', this.handleOutsideClick);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    this.applyCount();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.handleChange);
    document.removeEventListener('click', this.handleTierClick);
    document.removeEventListener('click', this.handleOutsideClick);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  /** @param {string} selector */
  readJson(selector) {
    try {
      const parsed = JSON.parse(this.querySelector(selector)?.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Taille choisie en haut de la fiche. */
  get globalSize() {
    return nativeValueIn(new Set(this.sizes));
  }

  /**
   * Pièce ajoutée : le coloris demandé, la taille du haut.
   * @param {string} colour
   * @returns {Piece}
   */
  newPiece(colour) {
    return { colour, size: this.globalSize, manual: false };
  }

  /** @param {Event} event */
  handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.name === 'pack_tier') {
      if (!this.syncing) this.resize(parseInt(target.dataset.quantity || '1', 10) || 1);
      return;
    }

    // Taille changée en haut : les lignes qui n'ont pas la leur la suivent.
    // Les coloris natifs changent sous nos propres clics, rien à faire.
    if (target.type === 'radio' && target.matches(NATIVE_INPUT) && this.sizes.includes(target.value)) {
      for (const piece of this.pieces) if (!piece.manual) piece.size = target.value;
      this.update();
    }
  };

  /**
   * Au-delà du plus grand palier, celui-ci reste coché : un clic dessus ne
   * déclenche aucun `change`. Le clic seul ramène alors au nombre du palier.
   * @param {Event} event
   */
  handleTierClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'pack_tier' || !target.checked) return;

    const quantity = parseInt(target.dataset.quantity || '1', 10) || 1;
    if (quantity !== this.pieces.length) this.resize(quantity);
  };

  /**
   * Taille changée à la main dans une ligne : elle ne suit plus celle du haut.
   * Sur une pièce seule, c'est la taille du haut qui change : le chemin natif
   * met au panier la variante de la fiche.
   * @param {number} index
   * @param {string} size
   */
  selectSize(index, size) {
    const piece = this.pieces[index];
    this.openSize = -1;
    if (!piece || !size) return;

    if (this.pieces.length === 1) {
      piece.manual = false;
      if (piece.size !== size) this.syncNative(size);
      // Le `change` du radio natif a déjà mis la ligne à jour ; sinon, on
      // referme seulement la liste.
      this.update(`[data-piece-size-toggle="${index}"]`);
      return;
    }

    piece.size = size;
    // Même ramenée à la taille du haut, une taille touchée à la main le reste.
    piece.manual = true;
    this.update(`[data-piece-size-toggle="${index}"]`);
  }

  /**
   * Ouvre ou ferme la liste des tailles d'une ligne, et place le focus : sur
   * la taille de la pièce à l'ouverture, sur le bouton à la fermeture.
   * @param {number} index
   */
  toggleSizes(index) {
    this.openSize = this.openSize === index ? -1 : index;
    this.render();
    this.focusAfterRender(
      this.openSize === index
        ? `[data-piece-sizes="${index}"] [aria-selected="true"]`
        : `[data-piece-size-toggle="${index}"]`
    );
  }

  /**
   * Un clic hors du composant referme la liste ouverte. Ceux du dedans sont
   * traités par `handleClick`. Le chemin de l'événement, et non `contains` :
   * le rendu a pu remplacer l'élément cliqué avant que le clic n'arrive ici.
   */
  handleOutsideClick = (/** @type {Event} */ event) => {
    if (this.openSize < 0 || event.composedPath().includes(this)) return;
    this.openSize = -1;
    this.render();
  };

  /**
   * Clavier de la liste des tailles, sur le modèle d'une liste déroulante :
   * flèches pour ouvrir et parcourir, Entrée ou Espace pour choisir, Échap
   * pour refermer sans changer, Tab pour sortir en refermant.
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.dataset.pieceSizeToggle !== undefined && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const index = Number(target.dataset.pieceSizeToggle);
      if (this.openSize !== index) this.toggleSizes(index);
      return;
    }

    if (target.dataset.pieceSizeOption === undefined) return;
    const index = Number(target.dataset.pieceIndex);
    const options = [...(target.parentElement?.children || [])].filter((node) => node instanceof HTMLElement);
    const position = options.indexOf(target);

    /** @param {number} next */
    const move = (next) => {
      event.preventDefault();
      const option = options[Math.max(0, Math.min(next, options.length - 1))];
      if (option instanceof HTMLElement) option.focus();
    };

    switch (event.key) {
      case 'ArrowDown':
        move(position + 1);
        break;
      case 'ArrowUp':
        move(position - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectSize(index, target.dataset.pieceSizeOption || '');
        break;
      case 'Escape':
        event.preventDefault();
        this.toggleSizes(index);
        break;
      case 'Tab':
        this.openSize = -1;
        this.render();
        this.focusAfterRender(`[data-piece-size-toggle="${index}"]`);
        break;
    }
  };

  /** @param {Event} event */
  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-piece-size-toggle]');
    if (toggle instanceof HTMLElement) {
      this.toggleSizes(Number(toggle.dataset.pieceSizeToggle));
      return;
    }

    const sizeOption = target.closest('[data-piece-size-option]');
    if (sizeOption instanceof HTMLElement) {
      this.selectSize(Number(sizeOption.dataset.pieceIndex), sizeOption.dataset.pieceSizeOption || '');
      return;
    }

    // Tout autre clic dans le composant referme la liste ouverte.
    if (this.openSize >= 0) {
      this.openSize = -1;
      this.render();
    }

    const swatch = target.closest('[data-piece-colour]');
    if (swatch instanceof HTMLElement) {
      const index = Number(swatch.dataset.pieceIndex);
      const colour = swatch.dataset.pieceColour || '';
      const piece = this.pieces[index];
      if (!piece || !colour || piece.colour === colour) return;
      piece.colour = colour;
      // La galerie montre le coloris qu'on vient de choisir. Sur une pièce
      // seule, c'est aussi la variante que le chemin natif mettra au panier.
      this.syncNative(colour);
      this.update(`[data-piece-index="${index}"][data-piece-colour="${CSS.escape(colour)}"]`);
      return;
    }

    const remove = target.closest('[data-piece-remove]');
    if (remove instanceof HTMLElement) {
      const index = Number(remove.dataset.pieceRemove);
      if (this.pieces.length <= 1) return;
      this.pieces.splice(index, 1);
      this.applyCount();
      // Le focus passe à la croix de la ligne qui prend la place, ou à « + ».
      const next =
        this.querySelector(`[data-piece-remove="${Math.min(index, this.pieces.length - 1)}"]`) || this.addButton;
      if (next instanceof HTMLElement) next.focus();
      return;
    }

    if (target.closest('[data-pieces-add]')) {
      if (this.pieces.length >= this.max) return;
      const last = this.pieces[this.pieces.length - 1];
      this.pieces.push(this.newPiece(last?.colour || this.options[0]?.name || ''));
      this.applyCount();
      if (this.addButton instanceof HTMLElement && !this.addButton.hidden) this.addButton.focus();
    }
  };

  /**
   * Nouveau nombre de pièces venu d'un palier. Les pièces gardent leur coloris
   * et leur taille, les nouvelles prennent le coloris de la dernière et la
   * taille du haut.
   * @param {number} quantity
   */
  resize(quantity) {
    const count = Math.max(1, Math.min(quantity, this.max));
    const colour = this.pieces[this.pieces.length - 1]?.colour || this.options[0]?.name || '';
    this.pieces = Array.from({ length: count }, (_, index) => this.pieces[index] || this.newPiece(colour));
    this.applyCount();
  }

  /** Coche le palier du nombre de pièces, puis republie et redessine. */
  applyCount() {
    const count = this.pieces.length;
    const tiers = [...document.querySelectorAll(TIER_INPUT)].filter((input) => input instanceof HTMLInputElement);
    const quantityOf = (/** @type {HTMLInputElement} */ input) => parseInt(input.dataset.quantity || '1', 10) || 1;

    const exact = tiers.find((input) => quantityOf(input) === count);
    // Au-delà : le plus grand palier sous ce nombre sert de référence.
    const below = tiers
      .filter((input) => quantityOf(input) < count)
      .sort((a, b) => quantityOf(b) - quantityOf(a))[0];
    const target = exact || below;

    document.documentElement.toggleAttribute('data-pack-beyond', !exact && tiers.length > 0);

    if (target instanceof HTMLInputElement && !target.checked) {
      this.syncing = true;
      target.checked = true;
      // Les autres composants — mode, prix, libellés — suivent le palier par
      // son `change`, comme après un clic.
      target.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncing = false;
    }

    // Une pièce seule part par le chemin natif : la fiche doit porter son
    // coloris et sa taille. Sa taille redevient alors celle du haut.
    if (count === 1) {
      const [piece] = this.pieces;
      this.syncNative(piece?.colour || '');
      if (piece?.manual) {
        piece.manual = false;
        this.syncNative(piece.size);
      }
    }

    this.update();
    document.dispatchEvent(new CustomEvent('pack:count', { detail: { count } }));
  }

  /**
   * Coche une valeur du picker natif — coloris ou taille —, par un clic, pour
   * que ce soit la mécanique du thème qui change variante et galerie.
   * @param {string} value
   */
  syncNative(value) {
    const input = value ? nativeInputFor(value) : null;
    if (input && !input.checked) input.click();
  }

  /** @param {string} [focusSelector] - Élément à refocaliser après le rendu. */
  update(focusSelector) {
    this.publish();
    this.render();
    this.showError('');
    if (focusSelector) this.focusAfterRender(focusSelector);
  }

  /** @param {string} selector */
  focusAfterRender(selector) {
    const next = this.querySelector(selector);
    if (next instanceof HTMLElement) next.focus();
  }

  publish() {
    const colourOrder = this.options.map((option) => option.name);
    const sizeOrder = this.sizes;

    /** @type {Map<string, number>} */
    const colours = new Map();
    /** @type {Map<string, { colour: string, size: string, quantity: number }>} */
    const lines = new Map();

    for (const piece of this.pieces) {
      colours.set(piece.colour, (colours.get(piece.colour) || 0) + 1);
      const key = `${piece.size}\u0000${piece.colour}`;
      const line = lines.get(key) || { colour: piece.colour, size: piece.size, quantity: 0 };
      line.quantity += 1;
      lines.set(key, line);
    }

    // Dans l'ordre de l'admin, quel que soit l'ordre des lignes : deux packs
    // identiques doivent s'écrire pareil au panier.
    const byColour = (/** @type {string} */ a, /** @type {string} */ b) => colourOrder.indexOf(a) - colourOrder.indexOf(b);
    const sortedLines = [...lines.values()].sort(
      (a, b) => sizeOrder.indexOf(a.size) - sizeOrder.indexOf(b.size) || byColour(a.colour, b.colour)
    );

    this.dataset.count = String(this.pieces.length);
    this.dataset.contents = [...colours]
      .sort((a, b) => byColour(a[0], b[0]))
      .map(([name, quantity]) => `${name}:${quantity}`)
      .join(', ');
    this.dataset.lines = JSON.stringify(sortedLines);
    this.dataset.mixed = String(lines.size > 1);
  }

  render() {
    if (!(this.list instanceof HTMLElement)) return;

    const itemLabel = this.dataset.itemLabel || '';
    const colourLabel = this.dataset.colourLabel || '';
    const sizeLabel = this.dataset.sizeLabel || '';
    const removeLabel = this.dataset.removeLabel || '';
    const removable = this.pieces.length > 1;

    const items = this.pieces.map((piece, index) => {
      const option = this.options.find((candidate) => candidate.name === piece.colour);
      const name = `${itemLabel} ${index + 1}`.trim();
      const item = createElement('li', 'pack-pieces__item');

      // Décorative : le coloris est écrit à côté, et nommé par sa pastille.
      const thumb = createElement('img', 'pack-pieces__thumb');
      thumb.alt = '';
      thumb.width = 58;
      thumb.height = 58;
      if (option?.image) thumb.src = option.image;
      item.append(thumb);

      if (removable) {
        const remove = createElement('button', 'pack-pieces__remove', '×');
        remove.type = 'button';
        remove.dataset.pieceRemove = String(index);
        remove.setAttribute('aria-label', `${removeLabel} ${name}`.trim());
        item.append(remove);
      }

      const body = createElement('div', 'pack-pieces__body');
      const title = createElement('span', 'pack-pieces__title', name);
      title.append(createElement('span', 'pack-pieces__value', piece.colour));

      const swatches = createElement('div', 'pack-pieces__swatches');
      swatches.setAttribute('role', 'group');
      swatches.setAttribute('aria-label', `${colourLabel} ${name}`.trim());

      for (const candidate of this.options) {
        const button = createElement('button', 'pack-pieces__swatch');
        button.type = 'button';
        button.dataset.pieceIndex = String(index);
        button.dataset.pieceColour = candidate.name;
        button.setAttribute('aria-label', candidate.name);
        button.setAttribute('aria-pressed', String(candidate.name === piece.colour));
        if (candidate.fill) button.style.setProperty('--pack-pieces-fill', candidate.fill);
        swatches.append(button);
      }

      body.append(title, swatches);
      item.append(body);

      // Liste déroulante maison et non <select> : le menu natif s'ouvre au
      // milieu de l'écran sur Android. Celle-ci s'ouvre sous son bouton, dans
      // la ligne. Décision du principal, 2026-09-27.
      // Réglage « Taille sur chaque ligne » décoché : pas de menu, la pièce
      // suit le sélecteur de taille de la fiche (2026-10-06).
      if (this.sizes.length && this.dataset.lineSizes !== 'false') {
        const open = this.openSize === index;
        const listId = `${this.uid}-sizes-${index}`;
        const wrap = createElement('div', 'pack-pieces__size');

        const toggle = createElement('button', 'pack-pieces__size-toggle', piece.size);
        toggle.type = 'button';
        toggle.dataset.pieceSizeToggle = String(index);
        toggle.setAttribute('aria-haspopup', 'listbox');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-controls', listId);
        toggle.setAttribute('aria-label', `${sizeLabel} ${name}, ${piece.size}`.trim());

        const list = createElement('ul', 'pack-pieces__size-list');
        list.id = listId;
        list.dataset.pieceSizes = String(index);
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', `${sizeLabel} ${name}`.trim());
        list.hidden = !open;

        for (const size of this.sizes) {
          const option = createElement('li', 'pack-pieces__size-option', size);
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', String(size === piece.size));
          option.tabIndex = -1;
          option.dataset.pieceIndex = String(index);
          option.dataset.pieceSizeOption = size;
          list.append(option);
        }

        wrap.append(toggle, list);
        item.append(wrap);
      }

      return item;
    });

    this.list.replaceChildren(...items);
    this.renderAdd();
  }

  /**
   * « + Ajouter un 3e débardeur », et ce que cette pièce apporte : son prix
   * (ce qu'elle coûte en plus pour la 2e, le prix par pièce ensuite), la
   * livraison offerte si elle franchit le seuil, et l'économie gagnée en
   * pastille au bout de la ligne.
   * Calculé sur la grille, pour rester juste au-delà des paliers.
   */
  renderAdd() {
    const button = this.addButton;
    if (!(button instanceof HTMLElement)) return;

    const next = this.pieces.length + 1;
    button.hidden = next > this.max;
    if (button.hidden) return;

    const title = button.querySelector('[data-pieces-add-title]');
    const sub = button.querySelector('[data-pieces-add-sub]');

    if (title instanceof HTMLElement) {
      const rank = document.createElement('span');
      const suffix = document.createElement('sup');
      suffix.textContent = 'e';
      rank.append(String(next), suffix);
      title.replaceChildren();
      fillTemplate(title, this.dataset.addLabel || '', '{n}', rank);
    }

    if (!(sub instanceof HTMLElement)) return;
    sub.replaceChildren();
    for (const node of button.querySelectorAll('[data-pieces-add-note], [data-pieces-add-chip]')) {
      if (node instanceof HTMLElement) node.hidden = true;
    }
    if (!this.grid.length) return;

    // La 2e pièce affiche le prix par pièce du Pack de 2 (27,90 €), qui va
    // avec la pastille -20 % de la tuile, et non ce que la 2e coûte en plus
    // (20,90 €, soit -40 %). Décision du principal, 2026-10-04 ; avant, le
    // surcoût de la 2e pièce (2026-09-27). Les suivantes affichent le total du
    // pack obtenu, « Les 3 pour 73,20 € » : un prix par pièce laissait croire
    // que la 3e s'ajoutait à ce prix. Décision du principal, 2026-09-27.
    const total = (/** @type {number} */ count) => pieceCents(this.grid, count) * count;
    const amount = document.createElement('strong');
    if (next === 2) {
      amount.textContent = formatMoney(pieceCents(this.grid, 2));
      fillTemplate(sub, this.dataset.addPrice || '', '{price}', amount);
    } else {
      amount.textContent = formatMoney(total(next));
      fillTemplate(sub, (this.dataset.addTotal || '').replace('{n}', String(next)), '{total}', amount);
    }

    // « Livraison offerte » sur sa propre ligne, sous le prix, quand la pièce
    // ajoutée franchit le seuil. Décision du principal, 2026-09-27.
    const note = button.querySelector('[data-pieces-add-note]');
    if (note instanceof HTMLElement) {
      const crosses = Boolean(this.shippingFrom) && next === this.shippingFrom;
      note.textContent = crosses ? this.dataset.shippingLabel || '' : '';
      note.hidden = !note.textContent;
    }

    // La remise gagnée, en pastille au bout de la ligne, sur le modèle du
    // « -10 % » relevé chez un concurrent. D'abord en euros (2026-09-27), le
    // brief proscrivant alors les pourcentages.
    //
    // Depuis le 2026-09-28, la pastille dit un pourcentage ({percent}) plutôt
    // que des euros ({amount}, toujours accepté). Depuis le 2026-10-04, c'est
    // toujours la remise du pack obtenu, la même que sur sa tuile : -20 % pour
    // 2 pièces, et non les -40 % de la 2e pièce seule. Un seul pourcentage par
    // offre sur la page. Décision du principal.
    const chip = button.querySelector('[data-pieces-add-chip]');
    if (chip instanceof HTMLElement) {
      const gained = savingCents(this.grid, next) - savingCents(this.grid, next - 1);
      const unit = pieceCents(this.grid, 1);
      const percent = percentOff(unit * next, total(next));
      const template = this.dataset.addSaving || '';
      const label = template.includes('{percent}')
        ? percent > 0
          ? template.replace('{percent}', String(percent))
          : ''
        : gained > 0
          ? template.replace('{amount}', formatCompactMoney(gained))
          : '';
      chip.replaceChildren();
      if (label) {
        const value = createElement('strong', 'pack-pieces__add-chip-amount');
        value.textContent = label;
        chip.append(value);
        const caption = this.dataset.addSavingCaption || '';
        if (caption) chip.append(createElement('span', 'pack-pieces__add-chip-caption', caption));
      }
      chip.hidden = !label;
    }
  }

  /** @param {string} message */
  showError(message) {
    const error = this.querySelector('[data-pieces-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }
}

// Le mode doit être posé même si aucun des blocs n'est présent sur la page.
applyMode(selectedTier());
syncDuoFromPage();
renderTierValue(selectedTier());

// Avant l'écouteur ci-dessous : les lignes par pièce ont leur nombre à jour
// quand il réécrit les libellés du palier.
if (!customElements.get('pack-pieces')) customElements.define('pack-pieces', PackPieces);

renderDuoLabel();
renderDuoImages(selectedTier());

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.name === 'pack_tier') {
    applyMode(target);
    renderDuoImages(target);
    renderTierValue(target);
  }
  if (target.name === 'pack_tier' || target.name === 'pack_duo') renderDuoLabel();

  if (target.name === 'pack_duo') syncNativeFromDuo(target);
  else if (target.type === 'radio' && target.matches(NATIVE_INPUT)) syncDuoFromNative(target);
});
